import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ShieldCheck,
  Building2,
  TrendingUp,
  DollarSign,
  Users,
  Search,
  LogOut,
  Gift,
  X,
  RotateCcw,
  ArrowUp,
  CheckCircle2,
  Loader2,
  Landmark,
  Calendar,
  Wallet,
  AlertCircle,
  Compass,
  UserCog,
  Package,
  User,
  Percent,
  ImagePlus,
  Save,
  Plus,
  Trash2,
  Power,
  PowerOff,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PLAN_LABEL = {
  free: { label: "Bronze", tone: "bg-slate-800 text-slate-300" },
  pro: { label: "Prata", tone: "bg-[#D4AF37]/15 text-[#D4AF37]" },
  grande_oriente: { label: "Ouro", tone: "bg-purple-900/40 text-purple-300" },
};

const brl = (v) =>
  Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 });

export const SuperAdminPanel = () => {
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [lojas, setLojas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [planFilter, setPlanFilter] = useState("");
  const [busy, setBusy] = useState(null); // {lojaId, action}
  const [detailsLoja, setDetailsLoja] = useState(null); // for modal
  const [selectedForCourtesy, setSelectedForCourtesy] = useState(null);
  const [selectedForDiscount, setSelectedForDiscount] = useState(null);
  const [activeTab, setActiveTab] = useState("lojas"); // lojas | planos | perfil
  const [adminProfile, setAdminProfile] = useState(null);

  const load = async () => {
    try {
      const [ovRes, ljRes] = await Promise.all([
        axios.get(`${API}/super-admin/overview`, { withCredentials: true }),
        axios.get(`${API}/super-admin/lojas`, {
          withCredentials: true,
          params: { search: q || undefined, plan: planFilter || undefined },
        }),
      ]);
      setOverview(ovRes.data);
      setLojas(ljRes.data.lojas);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        toast.error("Acesso restrito ao Super-Admin. Redirecionando…");
        setTimeout(() => navigate("/"), 1200);
      } else {
        toast.error("Erro ao carregar dados.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = "Super-Admin — Lojas Integradas";
    load();
    axios.get(`${API}/super-admin/profile`, { withCredentials: true })
      .then((response) => setAdminProfile(response.data))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refetch on search/filter change (debounced by manual submit for now)
  useEffect(() => {
    const t = setTimeout(load, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, planFilter]);

  const doGrantCourtesy = async (loja, months = 1, reason = "") => {
    setBusy({ lojaId: loja.id, action: "courtesy" });
    try {
      await axios.post(
        `${API}/super-admin/lojas/${loja.id}/grant-courtesy`,
        { months, reason },
        { withCredentials: true }
      );
      toast.success(`+${months} mês(es) de cortesia para ${loja.name}`);
      setSelectedForCourtesy(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha ao conceder cortesia.");
    } finally {
      setBusy(null);
    }
  };

  const doSetPlan = async (loja, planId) => {
    if (!window.confirm(`Confirma alterar o plano de ${loja.name} para ${planId}?`)) return;
    setBusy({ lojaId: loja.id, action: "plan" });
    try {
      await axios.post(
        `${API}/super-admin/lojas/${loja.id}/set-plan`,
        { plan_id: planId },
        { withCredentials: true }
      );
      toast.success(`Plano de ${loja.name} → ${planId}`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha ao alterar plano.");
    } finally {
      setBusy(null);
    }
  };

  const doCancel = async (loja) => {
    if (!window.confirm(`Cancelar a assinatura de ${loja.name}? A Loja volta para Free.`)) return;
    setBusy({ lojaId: loja.id, action: "cancel" });
    try {
      const res = await axios.post(
        `${API}/super-admin/lojas/${loja.id}/cancel-subscription`,
        {},
        { withCredentials: true }
      );
      toast.success(
        `${loja.name} cancelado.${res.data.canceled_stripe ? " (Stripe também)" : ""}`
      );
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha ao cancelar.");
    } finally {
      setBusy(null);
    }
  };

  const doImpersonate = async (loja) => {
    if (!window.confirm(`Entrar como Venerável de "${loja.name}" para dar suporte 1:1?\n\nA sessão de impersonação dura 1h e fica registrada na trilha de auditoria.`)) return;
    setBusy({ lojaId: loja.id, action: "impersonate" });
    try {
      const res = await axios.post(
        `${API}/super-admin/lojas/${loja.id}/impersonate`,
        {},
        { withCredentials: true }
      );
      toast.success(`Entrando como ${res.data.impersonating.user_name}…`);
      setTimeout(() => { window.location.href = "/painel"; }, 500);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha ao impersonar.");
      setBusy(null);
    }
  };

  const doToggleActive = async (loja) => {
    const willActivate = loja.is_active === false;
    const label = willActivate ? "ATIVAR" : "DESATIVAR";
    if (!window.confirm(`${label} a Loja "${loja.name}"?\n\n${willActivate ? "Os membros voltarão a poder fazer login." : "Todos os membros ficarão BLOQUEADOS de fazer login. Os dados são preservados — você pode reativar quando quiser."}`)) return;
    setBusy({ lojaId: loja.id, action: "toggle-active" });
    try {
      const res = await axios.patch(
        `${API}/super-admin/lojas/${loja.id}/toggle-active`,
        {},
        { withCredentials: true }
      );
      toast.success(`Loja "${loja.name}" ${res.data.is_active ? "ATIVADA" : "DESATIVADA"}.`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha ao alterar status da Loja.");
    } finally {
      setBusy(null);
    }
  };

  const doDeleteLoja = async (loja) => {
    const first = window.confirm(
      `⚠️ APAGAR PERMANENTEMENTE a Loja "${loja.name}"?\n\nTodos os dados serão APAGADOS em cascata:\n• Membros e usuários\n• Sessões, balaústres, boletins\n• Toda a Tesouraria (bancos, lançamentos, centros de custo)\n• Trilha de auditoria da Loja\n\nEssa ação é IRREVERSÍVEL.`
    );
    if (!first) return;
    const typed = window.prompt(
      `Para confirmar, digite EXATAMENTE o nome da Loja abaixo:\n\n"${loja.name}"`
    );
    if (typed !== loja.name) {
      toast.error("Nome não confere — exclusão cancelada.");
      return;
    }
    setBusy({ lojaId: loja.id, action: "delete" });
    try {
      const res = await axios.delete(
        `${API}/super-admin/lojas/${loja.id}?confirm=${encodeURIComponent(loja.id)}`,
        { withCredentials: true }
      );
      toast.success(res.data.message || `Loja "${loja.name}" apagada.`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha ao apagar a Loja.");
    } finally {
      setBusy(null);
    }
  };

  const openDetails = async (loja) => {
    try {
      const res = await axios.get(`${API}/super-admin/lojas/${loja.id}/details`, { withCredentials: true });
      setDetailsLoja(res.data);
    } catch {
      toast.error("Falha ao carregar detalhes.");
    }
  };

  const doLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch { /* ignore */ }
    navigate("/");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#080C14] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#080C14] text-[#F8FAFC] font-sans" data-testid="super-admin-panel">
      {/* Header */}
      <header className="border-b border-slate-800 bg-[#0a0f1a]/80 backdrop-blur-md sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-600 to-purple-900 flex items-center justify-center shadow-lg overflow-hidden">
              {adminProfile?.photo_b64 ? (
                <img src={adminProfile.photo_b64} alt="Logo do Super-Admin" className="w-full h-full object-cover" data-testid="sa-header-logo" />
              ) : (
                <ShieldCheck className="w-5 h-5 text-white" strokeWidth={2.5} data-testid="sa-header-default-icon" />
              )}
            </div>
            <div>
              <div className="text-sm font-serif font-bold text-white leading-tight">Super-Admin</div>
              <div className="text-[10px] tracking-widest uppercase text-purple-300">Lojas Integradas · Platform Console</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="sa-goto-home"
              onClick={() => navigate("/")}
              className="text-xs uppercase tracking-widest text-slate-400 hover:text-white px-3 py-2 rounded-lg hover:bg-slate-800 transition"
            >
              Home
            </button>
            <button
              data-testid="sa-logout"
              onClick={doLogout}
              className="flex items-center gap-2 text-xs uppercase tracking-widest bg-slate-800 hover:bg-slate-700 text-slate-100 px-4 py-2 rounded-lg transition"
            >
              <LogOut className="w-3.5 h-3.5" /> Sair
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        {/* Tabs Navigation */}
        <div className="flex gap-2 border-b border-slate-800 pb-1 overflow-x-auto">
          {[
            { k: "lojas", label: "Lojas & KPIs", icon: Building2 },
            { k: "planos", label: "Gerenciar Planos", icon: Package },
            { k: "perfil", label: "Meu Perfil (WebMaster)", icon: User },
          ].map((t) => {
            const Icon = t.icon;
            const active = activeTab === t.k;
            return (
              <button
                key={t.k}
                data-testid={`sa-tab-${t.k}`}
                onClick={() => setActiveTab(t.k)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-t-xl text-xs uppercase tracking-widest font-bold transition-all border-b-2 ${
                  active
                    ? "bg-[#0d1420] text-[#D4AF37] border-[#D4AF37]"
                    : "text-slate-400 border-transparent hover:text-white hover:bg-slate-900/40"
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {t.label}
              </button>
            );
          })}
        </div>

        {activeTab === "planos" && <PlansConfigTab onSaved={load} />}
        {activeTab === "perfil" && <SuperAdminProfileTab onProfileChange={setAdminProfile} />}

        {activeTab === "lojas" && (
        <>
        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KPI
            testid="sa-kpi-mrr"
            icon={DollarSign}
            label="MRR"
            value={brl(overview?.revenue?.mrr)}
            sublabel={`ARR ${brl(overview?.revenue?.arr)}`}
            accent="bg-emerald-500/15 text-emerald-300"
          />
          <KPI
            testid="sa-kpi-lojas"
            icon={Building2}
            label="Lojas cadastradas"
            value={overview?.totals?.lojas}
            sublabel={`+${overview?.totals?.new_lojas_30d} em 30 dias`}
            accent="bg-[#D4AF37]/15 text-[#D4AF37]"
          />
          <KPI
            testid="sa-kpi-conversion"
            icon={TrendingUp}
            label="Conversão"
            value={`${overview?.rates?.conversion_pct}%`}
            sublabel={`${overview?.totals?.paying_lojas} pagantes`}
            accent="bg-cyan-500/15 text-cyan-300"
          />
          <KPI
            testid="sa-kpi-churn"
            icon={AlertCircle}
            label="Churn"
            value={`${overview?.rates?.churn_pct}%`}
            sublabel={`${overview?.totals?.members} obreiros`}
            accent="bg-red-500/15 text-red-300"
          />
        </div>

        {/* Plan distribution */}
        <div className="rounded-2xl bg-[#0d1420] border border-slate-800 p-6">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-400 font-bold mb-4">
            <Users className="w-3.5 h-3.5" /> Distribuição por plano
          </div>
          <div className="grid grid-cols-3 gap-3">
            {["free", "pro", "grande_oriente"].map((p) => {
              const cfg = PLAN_LABEL[p];
              const count = overview?.plan_distribution?.[p] || 0;
              const total = overview?.totals?.lojas || 1;
              const pct = Math.round((count / total) * 100);
              return (
                <div key={p} data-testid={`sa-plan-${p}`} className="rounded-xl bg-slate-900/40 border border-slate-800 p-4">
                  <div className={`inline-block text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded ${cfg.tone}`}>
                    {cfg.label}
                  </div>
                  <div className="mt-2 text-2xl font-serif font-bold text-white">{count}</div>
                  <div className="text-xs text-slate-500">{pct}%</div>
                  <div className="mt-2 h-1.5 bg-slate-800 rounded-full overflow-hidden">
                    <div className="h-full bg-[#D4AF37]" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Search + filter */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <Search className="w-4 h-4 text-slate-500 absolute left-4 top-1/2 -translate-y-1/2" />
            <input
              data-testid="sa-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por nome, número, Oriente…"
              className="w-full pl-11 pr-4 py-3 rounded-xl bg-slate-900/60 border border-slate-800 focus:border-[#D4AF37] outline-none text-sm text-slate-100"
            />
          </div>
          <div className="flex gap-1.5 flex-wrap">
            {[
              { v: "", label: "Todos" },
              { v: "free", label: "Bronze" },
              { v: "pro", label: "Prata" },
              { v: "grande_oriente", label: "Ouro" },
            ].map((f) => (
              <button
                key={f.v}
                data-testid={`sa-filter-${f.v || "all"}`}
                onClick={() => setPlanFilter(f.v)}
                className={`text-xs uppercase tracking-widest px-3 py-2 rounded-lg font-semibold transition ${
                  planFilter === f.v
                    ? "bg-[#D4AF37] text-slate-950"
                    : "bg-slate-800 text-slate-300 hover:bg-slate-700"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Lojas table */}
        <div className="rounded-2xl bg-[#0d1420] border border-slate-800 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-800 bg-slate-900/40">
                  <th className="text-left px-4 py-3">Loja</th>
                  <th className="text-left px-4 py-3">Oriente</th>
                  <th className="text-left px-4 py-3">Plano</th>
                  <th className="text-right px-4 py-3">Obreiros</th>
                  <th className="text-right px-4 py-3">Cortesia</th>
                  <th className="text-center px-4 py-3">Ações</th>
                </tr>
              </thead>
              <tbody>
                {lojas.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-10 text-center text-slate-500">
                      Nenhuma Loja encontrada.
                    </td>
                  </tr>
                )}
                {lojas.map((l) => {
                  const cfg = PLAN_LABEL[l.plan_id] || PLAN_LABEL.free;
                  const isBusy = busy?.lojaId === l.id;
                  return (
                    <tr
                      key={l.id}
                      data-testid={`sa-loja-row-${l.id}`}
                      className="border-b border-slate-800/60 hover:bg-slate-900/30"
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {l.logo_b64 ? (
                            <img src={l.logo_b64} alt="" className="w-8 h-8 rounded object-cover" />
                          ) : (
                            <div className="w-8 h-8 rounded bg-slate-800 flex items-center justify-center">
                              <Landmark className="w-4 h-4 text-[#D4AF37]" />
                            </div>
                          )}
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-white truncate max-w-[220px]">{l.name}</span>
                              {l.is_active === false && (
                                <span
                                  data-testid={`sa-loja-inactive-badge-${l.id}`}
                                  className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded bg-rose-500/15 text-rose-300 border border-rose-500/40"
                                >
                                  Inativa
                                </span>
                              )}
                            </div>
                            <div className="text-[10px] text-slate-500 font-mono">nº {l.number || "—"}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-300 text-xs">{l.oriente || "—"}</td>
                      <td className="px-4 py-3">
                        <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded ${cfg.tone}`}>
                          {cfg.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-slate-200">{l.members_count}</td>
                      <td className="px-4 py-3 text-right">
                        {l.free_months_balance > 0 ? (
                          <span className="inline-flex items-center gap-1 text-xs bg-emerald-500/15 text-emerald-300 px-2 py-1 rounded font-mono">
                            <Gift className="w-3 h-3" /> {l.free_months_balance}
                          </span>
                        ) : (
                          <span className="text-slate-600">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            data-testid={`sa-action-details-${l.id}`}
                            onClick={() => openDetails(l)}
                            disabled={isBusy}
                            title="Detalhes"
                            className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 disabled:opacity-40"
                          >
                            <Building2 className="w-3.5 h-3.5" />
                          </button>
                          <button
                            data-testid={`sa-action-impersonate-${l.id}`}
                            onClick={() => doImpersonate(l)}
                            disabled={isBusy || l.members_count === 0}
                            title={l.members_count === 0 ? "Loja sem usuários" : "Impersonar (entrar como Venerável)"}
                            className="p-2 rounded-lg bg-purple-500/20 hover:bg-purple-500/40 text-purple-300 disabled:opacity-40"
                          >
                            <UserCog className="w-3.5 h-3.5" />
                          </button>
                          <button
                            data-testid={`sa-action-courtesy-${l.id}`}
                            onClick={() => setSelectedForCourtesy(l)}
                            disabled={isBusy}
                            title="Dar cortesia"
                            className="p-2 rounded-lg bg-emerald-500/20 hover:bg-emerald-500/40 text-emerald-300 disabled:opacity-40"
                          >
                            <Gift className="w-3.5 h-3.5" />
                          </button>
                          {l.plan_id !== "grande_oriente" ? (
                            <button
                              data-testid={`sa-action-upgrade-${l.id}`}
                              onClick={() => doSetPlan(l, l.plan_id === "free" ? "pro" : "grande_oriente")}
                              disabled={isBusy}
                              title={l.plan_id === "free" ? "Promover a Prata" : "Promover a Ouro"}
                              className="p-2 rounded-lg bg-[#D4AF37]/20 hover:bg-[#D4AF37]/40 text-[#D4AF37] disabled:opacity-40"
                            >
                              <ArrowUp className="w-3.5 h-3.5" />
                            </button>
                          ) : (
                            <button
                              data-testid={`sa-action-downgrade-${l.id}`}
                              onClick={() => doSetPlan(l, "pro")}
                              disabled={isBusy}
                              title="Downgrade para Pro"
                              className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 disabled:opacity-40"
                            >
                              <RotateCcw className="w-3.5 h-3.5" />
                            </button>
                          )}
                          {l.plan_id !== "free" && (
                            <button
                              data-testid={`sa-action-cancel-${l.id}`}
                              onClick={() => doCancel(l)}
                              disabled={isBusy}
                              title="Cancelar assinatura"
                              className="p-2 rounded-lg bg-red-500/20 hover:bg-red-500/40 text-red-300 disabled:opacity-40"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          )}
                          <button
                            data-testid={`sa-action-discount-${l.id}`}
                            onClick={() => setSelectedForDiscount(l)}
                            disabled={isBusy}
                            title={`Desconto individual${l.manual_discount_percent ? ` (${l.manual_discount_percent}% ativo)` : ""}`}
                            className={`p-2 rounded-lg ${
                              l.manual_discount_percent
                                ? "bg-cyan-500/40 text-cyan-100"
                                : "bg-cyan-500/20 hover:bg-cyan-500/40 text-cyan-300"
                            } disabled:opacity-40`}
                          >
                            <Percent className="w-3.5 h-3.5" />
                          </button>
                          <button
                            data-testid={`sa-action-toggle-active-${l.id}`}
                            onClick={() => doToggleActive(l)}
                            disabled={isBusy}
                            title={l.is_active === false ? "Ativar Loja (permitir login)" : "Desativar Loja (bloquear login sem apagar)"}
                            className={`p-2 rounded-lg ${
                              l.is_active === false
                                ? "bg-emerald-500/20 hover:bg-emerald-500/40 text-emerald-300"
                                : "bg-amber-500/20 hover:bg-amber-500/40 text-amber-300"
                            } disabled:opacity-40`}
                          >
                            {l.is_active === false ? <Power className="w-3.5 h-3.5" /> : <PowerOff className="w-3.5 h-3.5" />}
                          </button>
                          <button
                            data-testid={`sa-action-delete-${l.id}`}
                            onClick={() => doDeleteLoja(l)}
                            disabled={isBusy}
                            title="Apagar Loja permanentemente (cascade)"
                            className="p-2 rounded-lg bg-rose-500/20 hover:bg-rose-500/50 text-rose-300 disabled:opacity-40"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        </>
        )}
      </main>

      {/* Modal: Courtesy */}
      {selectedForCourtesy && (
        <CourtesyModal
          loja={selectedForCourtesy}
          onClose={() => setSelectedForCourtesy(null)}
          onGrant={(months, reason) => doGrantCourtesy(selectedForCourtesy, months, reason)}
        />
      )}

      {/* Modal: Details */}
      {detailsLoja && <DetailsModal data={detailsLoja} onClose={() => setDetailsLoja(null)} />}

      {/* Modal: Manual Discount */}
      {selectedForDiscount && (
        <ManualDiscountModal
          loja={selectedForDiscount}
          onClose={() => setSelectedForDiscount(null)}
          onSaved={() => {
            setSelectedForDiscount(null);
            load();
          }}
        />
      )}
    </div>
  );
};

const KPI = ({ testid, icon: Icon, label, value, sublabel, accent }) => (
  <div data-testid={testid} className="rounded-2xl bg-[#0d1420] border border-slate-800 p-5">
    <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${accent}`}>
      <Icon className="w-4 h-4" />
    </div>
    <div className="mt-3 text-2xl font-serif font-bold text-white leading-none">{value ?? "—"}</div>
    <div className="mt-1 text-[11px] uppercase tracking-widest text-slate-500">{label}</div>
    {sublabel && <div className="mt-1 text-[10px] text-slate-600">{sublabel}</div>}
  </div>
);

const CourtesyModal = ({ loja, onClose, onGrant }) => {
  const [months, setMonths] = useState(1);
  const [reason, setReason] = useState("");
  return (
    <div className="fixed inset-0 z-30 bg-black/70 flex items-center justify-center p-4" data-testid="courtesy-modal">
      <div className="w-full max-w-md bg-[#0d1420] border border-emerald-700/60 rounded-2xl shadow-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-500/15 flex items-center justify-center">
              <Gift className="w-5 h-5 text-emerald-300" />
            </div>
            <div>
              <div className="text-sm font-serif font-bold text-white">Dar cortesia</div>
              <div className="text-xs text-slate-500 truncate max-w-[280px]">{loja.name}</div>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-800 rounded">
            <X className="w-4 h-4 text-slate-400" />
          </button>
        </div>
        <label className="block mb-4">
          <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Meses grátis</span>
          <input
            data-testid="courtesy-input-months"
            type="number"
            min="1"
            max="60"
            value={months}
            onChange={(e) => setMonths(parseInt(e.target.value || "1"))}
            className="mt-1.5 w-full px-4 py-2.5 rounded-xl bg-slate-900/60 border border-slate-800 focus:border-emerald-400 outline-none text-sm text-slate-100"
          />
        </label>
        <label className="block mb-6">
          <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Motivo (opcional)</span>
          <textarea
            data-testid="courtesy-input-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            placeholder="Ex: parceria institucional / retenção…"
            className="mt-1.5 w-full px-4 py-2.5 rounded-xl bg-slate-900/60 border border-slate-800 focus:border-emerald-400 outline-none text-sm text-slate-100 resize-none"
          />
        </label>
        <div className="flex justify-end gap-2">
          <button
            data-testid="courtesy-cancel"
            onClick={onClose}
            className="px-4 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs uppercase tracking-widest font-semibold"
          >
            Cancelar
          </button>
          <button
            data-testid="courtesy-confirm"
            onClick={() => onGrant(months, reason)}
            className="px-5 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-slate-950 text-xs uppercase tracking-widest font-bold flex items-center gap-2"
          >
            <CheckCircle2 className="w-3.5 h-3.5" /> Conceder
          </button>
        </div>
      </div>
    </div>
  );
};

const DetailsModal = ({ data, onClose }) => {
  const l = data.loja;
  return (
    <div className="fixed inset-0 z-30 bg-black/70 flex items-center justify-center p-4" data-testid="details-modal">
      <div className="w-full max-w-lg bg-[#0d1420] border border-slate-700 rounded-2xl shadow-2xl p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-lg bg-[#D4AF37]/15 flex items-center justify-center">
              <Landmark className="w-5 h-5 text-[#D4AF37]" />
            </div>
            <div>
              <div className="text-lg font-serif font-bold text-white">{l.name}</div>
              <div className="text-xs text-slate-500">nº {l.number || "—"} · {l.oriente}</div>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-800 rounded">
            <X className="w-4 h-4 text-slate-400" />
          </button>
        </div>
        <div className="grid grid-cols-2 gap-3 mb-5">
          <MiniStat icon={Users} label="Obreiros" value={data.members_count} />
          <MiniStat icon={Calendar} label="Sessões" value={data.sessions_count} />
          <MiniStat icon={Wallet} label="Saldo" value={brl(data.balance)} />
          <MiniStat icon={Gift} label="Cortesia" value={l.free_months_balance || 0} />
        </div>
        <div className="pt-4 border-t border-slate-800 text-xs space-y-2">
          <Row k="Plano" v={PLAN_LABEL[l.plan_id || "free"].label} />
          <Row k="Venerável" v={data.veneravel ? `${data.veneravel.name} (${data.veneravel.email})` : "—"} />
          <Row k="Assinou em" v={l.subscribed_at ? new Date(l.subscribed_at).toLocaleDateString("pt-BR") : "—"} />
          <Row k="Criada em" v={l.created_at ? new Date(l.created_at).toLocaleDateString("pt-BR") : "—"} />
          <Row k="Cód. indicação" v={l.referral_code || "—"} />
          <Row k="Indicada por" v={l.referred_by_loja_id || "—"} />
        </div>
      </div>
    </div>
  );
};

const MiniStat = ({ icon: Icon, label, value }) => (
  <div className="rounded-xl bg-slate-900/40 border border-slate-800 p-3">
    <div className="flex items-center gap-2">
      <Icon className="w-3.5 h-3.5 text-[#D4AF37]" />
      <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{label}</span>
    </div>
    <div className="mt-1 text-lg font-serif font-bold text-white">{value}</div>
  </div>
);

const Row = ({ k, v }) => (
  <div className="flex items-center justify-between gap-4">
    <span className="text-slate-500">{k}</span>
    <span className="text-slate-200 truncate max-w-[280px]">{v}</span>
  </div>
);

// ==========================================
// Manual Discount Modal (per Loja)
// ==========================================

const ManualDiscountModal = ({ loja, onClose, onSaved }) => {
  const [pct, setPct] = useState(loja.manual_discount_percent || 0);
  const [reason, setReason] = useState(loja.manual_discount_reason || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/super-admin/lojas/${loja.id}/manual-discount`, {
        manual_discount_percent: parseFloat(pct) || 0,
        reason: reason.trim(),
      });
      toast.success(pct > 0 ? `Desconto de ${pct}% aplicado.` : "Desconto removido.");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar desconto.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-30 bg-black/70 flex items-center justify-center p-4" data-testid="discount-modal">
      <div className="w-full max-w-md bg-[#0d1420] border border-cyan-700/60 rounded-2xl shadow-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-cyan-500/15 flex items-center justify-center">
              <Percent className="w-5 h-5 text-cyan-300" />
            </div>
            <div>
              <div className="text-sm font-serif font-bold text-white">Desconto individual</div>
              <div className="text-xs text-slate-500 truncate max-w-[280px]">{loja.name}</div>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-800 rounded">
            <X className="w-4 h-4 text-slate-400" />
          </button>
        </div>
        <label className="block mb-4">
          <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">% de desconto (0-100)</span>
          <input
            data-testid="discount-input-pct"
            type="number"
            min="0"
            max="100"
            step="0.5"
            value={pct}
            onChange={(e) => setPct(e.target.value)}
            className="mt-1.5 w-full px-4 py-2.5 rounded-xl bg-slate-900/60 border border-slate-800 focus:border-cyan-400 outline-none text-sm text-slate-100"
          />
        </label>
        <label className="block mb-4">
          <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Motivo (opcional)</span>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Ex: parceria estratégica, fidelidade, cortesia..."
            className="mt-1.5 w-full px-4 py-2.5 rounded-xl bg-slate-900/60 border border-slate-800 focus:border-cyan-400 outline-none text-sm text-slate-100"
          />
        </label>
        <div className="text-[10px] text-slate-500 italic mb-4">
          O desconto é aplicado sobre a próxima renovação. Coloque 0 pra remover.
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-xl bg-slate-800 text-slate-200 text-xs font-bold uppercase">
            Cancelar
          </button>
          <button
            data-testid="discount-btn-save"
            onClick={save}
            disabled={saving}
            className="px-5 py-2 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-slate-950 text-xs font-bold uppercase disabled:opacity-40"
          >
            {saving ? "Salvando..." : "Salvar"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ==========================================
// Plans Config Tab
// ==========================================

const PlansConfigTab = ({ onSaved }) => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/super-admin/plans-config`)
      .then((r) => setPlans(r.data.plans))
      .catch(() => toast.error("Erro ao carregar planos."))
      .finally(() => setLoading(false));
  }, []);

  const updatePlan = (idx, patch) => {
    setPlans((prev) => prev.map((p, i) => (i === idx ? { ...p, ...patch } : p)));
  };

  const addFeature = (idx) => updatePlan(idx, { features: [...(plans[idx].features || []), ""] });
  const updateFeature = (idx, fi, val) => {
    const feats = [...plans[idx].features];
    feats[fi] = val;
    updatePlan(idx, { features: feats });
  };
  const removeFeature = (idx, fi) => {
    updatePlan(idx, { features: plans[idx].features.filter((_, i) => i !== fi) });
  };

  const save = async () => {
    setSaving(true);
    try {
      // Normalize numeric fields
      const clean = plans.map((p) => ({
        ...p,
        price_monthly_brl: parseFloat(p.price_monthly_brl) || 0,
        price_yearly_brl: parseFloat(p.price_yearly_brl) || 0,
        annual_discount_percent: parseFloat(p.annual_discount_percent) || 0,
        features: (p.features || []).filter((f) => f.trim()),
      }));
      const res = await axios.put(`${API}/super-admin/plans-config`, { plans: clean });
      setPlans(res.data.plans);
      toast.success("Planos atualizados! A página pública já reflete as mudanças.");
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar planos.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-10 text-slate-400 text-sm">
        <Loader2 className="w-6 h-6 animate-spin inline mr-2" />
        Carregando planos...
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="plans-config-tab">
      <div className="rounded-2xl bg-[#0d1420] border border-slate-800 p-5">
        <div className="flex items-start justify-between gap-4 mb-2">
          <div>
            <div className="text-xs uppercase tracking-widest text-[#D4AF37] font-bold mb-1">Configuração de Planos</div>
            <h2 className="text-xl font-serif font-bold text-white">Preços, Descontos & Benefícios</h2>
            <p className="text-xs text-slate-500 mt-1">
              Edite preços mensais, anuais, descontos e benefícios de cada plano. As mudanças aparecem imediatamente
              na página pública <code className="text-[#D4AF37]">/pricing</code>.
            </p>
          </div>
          <button
            data-testid="plans-btn-save"
            onClick={save}
            disabled={saving}
            className="flex items-center gap-2 bg-[#D4AF37] hover:bg-[#C5A028] text-slate-950 font-bold px-5 py-2.5 rounded-xl text-xs uppercase tracking-widest shadow-[0_0_15px_rgba(212,175,55,0.25)] disabled:opacity-40"
          >
            <Save className="w-3.5 h-3.5" />
            {saving ? "Salvando..." : "Salvar todos"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {plans.map((p, idx) => (
          <div
            key={p.id}
            data-testid={`plan-card-${p.id}`}
            className={`rounded-2xl bg-[#0d1420] border-2 p-5 ${
              p.highlighted ? "border-[#D4AF37]/60" : "border-slate-800"
            }`}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-[#D4AF37]/15 text-[#D4AF37] flex items-center justify-center text-xs font-mono font-bold">
                  {p.id.substring(0, 2).toUpperCase()}
                </div>
                <div>
                  <input
                    data-testid={`plan-input-name-${p.id}`}
                    value={p.display_name}
                    onChange={(e) => updatePlan(idx, { display_name: e.target.value })}
                    className="bg-transparent text-lg font-serif font-bold text-white border-b border-slate-800 focus:border-[#D4AF37] outline-none w-full"
                  />
                  <div className="text-[10px] text-slate-500 uppercase tracking-widest font-mono">ID: {p.id}</div>
                </div>
              </div>
              <label className="flex items-center gap-1 text-[10px] text-slate-400 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!p.public}
                  onChange={(e) => updatePlan(idx, { public: e.target.checked })}
                  className="w-3 h-3 accent-[#D4AF37]"
                />
                Público
              </label>
            </div>

            <input
              value={p.tagline || ""}
              onChange={(e) => updatePlan(idx, { tagline: e.target.value })}
              placeholder="Tagline curta"
              className="w-full bg-slate-900/40 border border-slate-800 rounded-lg px-3 py-2 text-xs text-slate-200 mb-3 focus:border-[#D4AF37] outline-none"
            />

            <div className="grid grid-cols-2 gap-2 mb-3">
              <label className="block">
                <span className="text-[9px] uppercase tracking-widest text-slate-500 font-bold">Mensal (R$)</span>
                <input
                  data-testid={`plan-input-monthly-${p.id}`}
                  type="number"
                  min="0"
                  value={p.price_monthly_brl}
                  onChange={(e) => updatePlan(idx, { price_monthly_brl: e.target.value })}
                  className="mt-1 w-full px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-800 text-sm text-white font-mono focus:border-[#D4AF37] outline-none"
                />
              </label>
              <label className="block">
                <span className="text-[9px] uppercase tracking-widest text-slate-500 font-bold">Anual (R$)</span>
                <input
                  data-testid={`plan-input-yearly-${p.id}`}
                  type="number"
                  min="0"
                  value={p.price_yearly_brl}
                  onChange={(e) => updatePlan(idx, { price_yearly_brl: e.target.value })}
                  className="mt-1 w-full px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-800 text-sm text-white font-mono focus:border-[#D4AF37] outline-none"
                />
              </label>
            </div>

            <label className="block mb-3">
              <span className="text-[9px] uppercase tracking-widest text-slate-500 font-bold flex items-center gap-1">
                <Percent className="w-3 h-3" /> Desconto anual (%)
              </span>
              <input
                data-testid={`plan-input-discount-${p.id}`}
                type="number"
                min="0"
                max="100"
                value={p.annual_discount_percent || 0}
                onChange={(e) => updatePlan(idx, { annual_discount_percent: e.target.value })}
                className="mt-1 w-full px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-800 text-sm text-white font-mono focus:border-[#D4AF37] outline-none"
              />
            </label>

            <div className="mb-2">
              <div className="text-[9px] uppercase tracking-widest text-slate-500 font-bold mb-2 flex items-center justify-between">
                <span>Benefícios</span>
                <button
                  data-testid={`plan-btn-add-feature-${p.id}`}
                  onClick={() => addFeature(idx)}
                  className="text-[#D4AF37] hover:text-white flex items-center gap-1"
                >
                  <Plus className="w-3 h-3" /> Adicionar
                </button>
              </div>
              <div className="space-y-1.5">
                {(p.features || []).map((f, fi) => (
                  <div key={fi} className="flex gap-1">
                    <input
                      value={f}
                      onChange={(e) => updateFeature(idx, fi, e.target.value)}
                      className="flex-1 bg-slate-900/40 border border-slate-800 rounded-lg px-2 py-1.5 text-xs text-slate-200 focus:border-[#D4AF37] outline-none"
                    />
                    <button
                      onClick={() => removeFeature(idx, fi)}
                      className="p-1.5 rounded-lg bg-red-500/10 hover:bg-red-500/30 text-red-400"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <label className="flex items-center gap-1 text-[10px] text-slate-400 mt-3 cursor-pointer">
              <input
                type="checkbox"
                checked={!!p.highlighted}
                onChange={(e) => updatePlan(idx, { highlighted: e.target.checked })}
                className="w-3 h-3 accent-[#D4AF37]"
              />
              Destacar como "recomendado"
            </label>
          </div>
        ))}
      </div>
    </div>
  );
};

// ==========================================
// Super-Admin Profile Tab
// ==========================================

const SuperAdminProfileTab = ({ onProfileChange }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });

  useEffect(() => {
    axios
      .get(`${API}/super-admin/profile`)
      .then((r) => {
        setProfile(r.data);
        onProfileChange?.(r.data);
      })
      .catch(() => toast.error("Erro ao carregar perfil."))
      .finally(() => setLoading(false));
  }, []);

  const setField = (patch) => setProfile((p) => ({ ...(p || {}), ...patch }));

  const handlePhotoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024) {
      toast.error("Foto muito grande (máx 500 KB).");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => setField({ photo_b64: ev.target.result });
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: profile.name,
        email: profile.email,
        photo_b64: profile.photo_b64,
        address: profile.address,
        phone: profile.phone,
      };
      if (pw.next) {
        if (pw.next !== pw.confirm) {
          toast.error("Nova senha e confirmação não conferem.");
          setSaving(false);
          return;
        }
        payload.current_password = pw.current;
        payload.new_password = pw.next;
      }
      const res = await axios.put(`${API}/super-admin/profile`, payload);
      setProfile(res.data.profile);
      onProfileChange?.(res.data.profile);
      setPw({ current: "", next: "", confirm: "" });
      toast.success("Perfil atualizado com sucesso!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao atualizar perfil.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-10 text-slate-400 text-sm">
        <Loader2 className="w-6 h-6 animate-spin inline mr-2" />
        Carregando perfil...
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="sa-profile-tab">
      <div className="rounded-2xl bg-[#0d1420] border border-slate-800 p-6">
        <div className="text-xs uppercase tracking-widest text-[#D4AF37] font-bold mb-1">Meu Perfil</div>
        <h2 className="text-xl font-serif font-bold text-white">WebMaster — Dono do Sistema</h2>
        <p className="text-xs text-slate-500 mt-1">
          Suas informações pessoais como administrador da plataforma. Isso não interfere em nenhuma Loja individual.
        </p>
      </div>

      <div className="rounded-2xl bg-[#0d1420] border border-slate-800 p-6 space-y-5">
        {/* Photo */}
        <div className="flex items-center gap-5">
          <div className="w-24 h-24 rounded-full bg-slate-900 border-2 border-[#D4AF37]/40 overflow-hidden flex items-center justify-center">
            {profile?.photo_b64 ? (
              <img src={profile.photo_b64} alt="Foto" className="w-full h-full object-cover" data-testid="sa-profile-photo" />
            ) : (
              <User className="w-10 h-10 text-slate-600" />
            )}
          </div>
          <div>
            <label className="inline-flex items-center gap-2 border-2 border-dashed border-[#D4AF37]/40 hover:border-[#D4AF37] text-[#D4AF37] px-4 py-2 rounded-lg cursor-pointer text-xs font-bold uppercase tracking-widest transition-all">
              <ImagePlus className="w-3.5 h-3.5" />
              {profile?.photo_b64 ? "Trocar foto" : "Enviar foto"}
              <input
                data-testid="sa-profile-photo-input"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handlePhotoUpload}
              />
            </label>
            {profile?.photo_b64 && (
              <button
                onClick={() => setField({ photo_b64: null })}
                className="ml-3 text-[10px] uppercase font-bold text-rose-400 hover:text-rose-300 tracking-widest"
              >
                Remover
              </button>
            )}
          </div>
        </div>

        {/* Fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Nome</span>
            <input
              data-testid="sa-profile-name"
              value={profile?.name || ""}
              onChange={(e) => setField({ name: e.target.value })}
              className="mt-1 w-full px-4 py-2.5 rounded-xl bg-slate-900/60 border border-slate-800 text-sm text-white focus:border-[#D4AF37] outline-none"
            />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">E-mail</span>
            <input
              data-testid="sa-profile-email"
              type="email"
              value={profile?.email || ""}
              onChange={(e) => setField({ email: e.target.value })}
              className="mt-1 w-full px-4 py-2.5 rounded-xl bg-slate-900/60 border border-slate-800 text-sm text-white focus:border-[#D4AF37] outline-none"
            />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Telefone</span>
            <input
              value={profile?.phone || ""}
              onChange={(e) => setField({ phone: e.target.value })}
              className="mt-1 w-full px-4 py-2.5 rounded-xl bg-slate-900/60 border border-slate-800 text-sm text-white focus:border-[#D4AF37] outline-none"
            />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Endereço</span>
            <input
              value={profile?.address || ""}
              onChange={(e) => setField({ address: e.target.value })}
              className="mt-1 w-full px-4 py-2.5 rounded-xl bg-slate-900/60 border border-slate-800 text-sm text-white focus:border-[#D4AF37] outline-none"
            />
          </label>
        </div>

        {/* Password */}
        <div className="pt-4 border-t border-slate-800">
          <div className="text-[11px] uppercase tracking-widest text-slate-400 font-bold mb-3">Alterar senha</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <input
              type="password"
              placeholder="Senha atual"
              value={pw.current}
              onChange={(e) => setPw({ ...pw, current: e.target.value })}
              className="px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-800 text-sm text-white focus:border-[#D4AF37] outline-none"
            />
            <input
              type="password"
              placeholder="Nova senha (≥6)"
              value={pw.next}
              onChange={(e) => setPw({ ...pw, next: e.target.value })}
              className="px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-800 text-sm text-white focus:border-[#D4AF37] outline-none"
            />
            <input
              type="password"
              placeholder="Confirmar nova senha"
              value={pw.confirm}
              onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
              className="px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-800 text-sm text-white focus:border-[#D4AF37] outline-none"
            />
          </div>
        </div>

        <div className="flex justify-end pt-3 border-t border-slate-800">
          <button
            data-testid="sa-profile-save"
            onClick={save}
            disabled={saving}
            className="flex items-center gap-2 bg-[#D4AF37] hover:bg-[#C5A028] text-slate-950 font-bold px-6 py-2.5 rounded-xl text-xs uppercase tracking-widest shadow-[0_0_15px_rgba(212,175,55,0.25)] disabled:opacity-40"
          >
            <Save className="w-3.5 h-3.5" />
            {saving ? "Salvando..." : "Salvar Perfil"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminPanel;
